import MultiComboboxField from './MultiComboboxField';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
  Autocomplete,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { enterAdvanceProps } from '../../utils/enterAdvance';
import FlexService from '../../services/FlexService';
import { humanizeEntityName } from '../../utils/entityLabels';
import EntityPickerOverlay from './EntityPickerOverlay';
import AddCustomFieldDialog, { canAddCustomField, foldCustomFields } from './AddCustomFieldDialog';
import fieldRendererRegistry from './registry/fieldRendererRegistry';
import { isFieldShownFor } from './fieldVisibility';

// A date-and-time field: the input shows the user's own local time (2026-10-30T09:00); the warehouse system is sent
// and returns the moment itself (2026-10-30T13:00:00.000Z). A time with no zone used to go out as typed and was dropped.
const pad2 = (n) => String(n).padStart(2, '0');
export const toDateTimeInput = (v) => {
  if (!v || typeof v !== 'string' || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v)) return v;
  // a date alone (a TODAY default) is the start of that day here, not midnight UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
export const fromDateTimeInput = (v) => {
  if (!v || typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v)) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toISOString();
};

/**
 * EntityWizardOverlay - Full-workspace overlay for creating/editing entities
 * with master-detail support (e.g., Receipt with ReceiptLines).
 *
 * Covers the workspace area. Renders header fields from entity metadata,
 * plus an optional repeatable detail lines section.
 */
const EntityWizardOverlay = ({
  entityName,          // e.g., 'Receipt'
  detailEntityName,    // e.g., 'ReceiptLine' (optional — if present, shows lines section)
  wizardKey,           // e.g., 'create_receipt_wizard'
  rowData,             // null for create, existing data for edit -- OR seed data for a
                       // create form that still needs isEdit=false (see forceCreate)
  scriptName,          // optional FLEX script name to call on save (overrides default Entity.Create)
  title,               // optional custom title (e.g., 'Receive Inventory')
  saveLabel,           // optional custom save button label (e.g., 'Receive')
  colors,
  onSave,              // (result) => void
  onClose,
  embedded = false,    // true = renders inline (not as overlay), hides close/cancel buttons
  forceCreate = false, // true = treat as create even though rowData is non-null (prefill/nav-scope
                       // seed data, e.g. Zones' "Add Location" seeding zoneId from navParams) --
                       // without this, any non-null rowData is indistinguishable from "editing an
                       // existing record", which incorrectly disables is_editable=false fields.
  fieldNames = null,   // only these fields of the entity, in this order (a wizard step asks a few of its entity's fields)
  buildPayload = null, // (payload) => the _payload to send, e.g. a wizard step's answers wrapped with the run (FH-364)
}) => {
  const isEdit = !!rowData && !forceCreate;
  const [headerFields, setHeaderFields] = useState([]);
  const [lineFields, setLineFields] = useState([]);
  const [headerValues, setHeaderValues] = useState({});
  const [lines, setLines] = useState([]);
  // the whole form, for validation Commands that check one field against others (FH-253: custom field rules)
  const formValuesRef = useRef({ headerValues: {}, lines: [] });
  formValuesRef.current = { headerValues, lines };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [lineErrors, setLineErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [validating, setValidating] = useState({}); // { fieldName: true } while async validation in progress
  const [hiddenFieldDefaults, setHiddenFieldDefaults] = useState({}); // Hidden fields with defaults to include in payload
  const [entityDisplayName, setEntityDisplayName] = useState(''); // entity_registry displayName from the fields API
  const entityLabel = entityDisplayName || humanizeEntityName(entityName);
  const [dynamicOptions, setDynamicOptions] = useState({}); // { fieldName: [{value, label}] } from optionsScript
  const [pickerOpen, setPickerOpen] = useState(false); // Inline entity picker overlay
  const [pickerField, setPickerField] = useState(null); // Field config that triggered the picker
  const [resolvedPickers, setResolvedPickers] = useState({}); // { componentCode: pickerConfig }
  const [dynamicFieldOverrides, setDynamicFieldOverrides] = useState({}); // { fieldName: { isRequired: true } }
  const [dependentFields, setDependentFields] = useState([]); // Additional fields loaded via dependentFieldsScript
  const [dependentFieldsLoading, setDependentFieldsLoading] = useState(false);
  const touchedHeaderFieldsRef = useRef(new Set()); // fieldNames the user has manually edited -- defaultValueScript never overwrites these
  const defaultScriptDebounceRef = useRef(null);
  const acDebounceRef = useRef({}); // { fieldName: timeoutId } for searchDynamicOptions below
  // { fieldName: n } -- each options request for a field gets the next number and only the newest
  // may land: the slow unfiltered first load used to arrive after a typed search and replace it,
  // so a location typed straight away was never offered (FH-236).
  const optionsSeqRef = useRef({});
  // "Add custom field" (FH-202): shown to people allowed to add one; adding re-reads the fields
  const [mayAddCustomField, setMayAddCustomField] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [fieldsVersion, setFieldsVersion] = useState(0);
  const addedFieldRef = useRef(null); // the field just added: brought into view and focused once it renders
  useEffect(() => {
    let cancelled = false;
    canAddCustomField(entityName).then((ok) => { if (!cancelled) setMayAddCustomField(ok); });
    return () => { cancelled = true; };
  }, [entityName]);
  useEffect(() => {
    const name = addedFieldRef.current;
    if (!name || !headerFields.some((f) => f.fieldName === name)) return;
    addedFieldRef.current = null;
    const box = document.querySelector(`[data-field="${name}"]`);
    if (box) {
      box.scrollIntoView({ block: 'center' });
      const input = box.querySelector('input, [role="combobox"]');
      if (input) input.focus();
    }
  }, [headerFields]);

  // Live re-search for an AUTOCOMPLETE field as the user types -- previously
  // AUTOCOMPLETE options were only ever loaded once on mount (the effect
  // above), so typing could only client-side-filter that one static batch.
  // For an options script whose result set is larger than what the server
  // returns by default (e.g. staging_location_picker across every isStaging
  // area), the location the user is actually looking for may never have been
  // in that first batch at all -- confirmed live: typing "SMP-STAGE" into the
  // Allocate form's Staging Location field matched zero options even though
  // real SMP-STAGE-4-* locations exist, because the initial unfiltered load
  // never included them. Mirrors RapidEntryOverlay's own working searchOptions.
  const searchDynamicOptions = useCallback((field, searchText) => {
    if (!field.optionsScript) return;
    const fname = field.fieldName;
    if (acDebounceRef.current[fname]) clearTimeout(acDebounceRef.current[fname]);
    acDebounceRef.current[fname] = setTimeout(async () => {
      const seq = (optionsSeqRef.current[fname] || 0) + 1;
      optionsSeqRef.current[fname] = seq;
      try {
        const response = await FlexService.executeScript({
          scriptName: field.optionsScript,
          context: { _payload: { ...(rowData || {}), search: searchText || '', limit: 20, _field: fname, _entity: entityName } },
        });
        if (optionsSeqRef.current[fname] !== seq) return; // a newer search has been sent
        if (response.success) {
          const ctx = response.context || {};
          const opts = ctx.result || ctx.data || [];
          if (Array.isArray(opts)) {
            setDynamicOptions((prev) => ({ ...prev, [fname]: opts }));
          }
        }
      } catch (err) {
        console.error(`Search failed for ${fname}:`, err);
      }
    }, 300);
  }, [rowData, entityName]);

  // Fetch field metadata for header + detail entities
  useEffect(() => {
    const fetchMeta = async () => {
      // re-reading after "Add custom field" keeps the form on screen (no spinner, no jump to the top)
      if (!addedFieldRef.current) setLoading(true);
      try {
        const token = localStorage.getItem('waide_access_token');
        const fetchFields = async (entity) => {
          const res = await fetch(`/api/v1/catalog/${entity}/fields`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return [];
          const data = await res.json();
          return (data.fields || data || [])
            .filter((f) => f.isVisibleInForm !== false)
            // hiddenOnEdit: a field (e.g. a "Clone From" picker) that only
            // makes sense once, at creation -- isVisibleInForm alone can't
            // distinguish Create from Edit, so this is the one escape hatch.
            .filter((f) => !(f.hiddenOnEdit === true && isEdit))
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        };

        let hFields = await fetchFields(entityName);
        if (Array.isArray(fieldNames) && fieldNames.length) {
          hFields = fieldNames.map((name) => hFields.find((f) => f.fieldName === name)).filter(Boolean);
        }
        setHeaderFields(hFields);

        // Also fetch hidden fields with defaults (for system fields like isAutoGeneratedId)
        const resAll = await fetch(`/api/v1/catalog/${entityName}/fields`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('waide_access_token')}` },
        });
        const allFieldsData = resAll.ok ? await resAll.json() : {};
        setEntityDisplayName(allFieldsData.displayName || '');
        const hiddenDefaults = {};
        (allFieldsData.fields || allFieldsData || [])
          .filter((f) => f.isVisibleInForm === false && f.defaultValue && f.wmsPayloadPath)
          .forEach((f) => {
            hiddenDefaults[f.wmsPayloadPath || f.fieldName] = f.defaultValue;
          });
        setHiddenFieldDefaults(hiddenDefaults);

        // Initialize header values
        const initial = {};
        hFields.forEach((f) => {
          let val = rowData?.[f.fieldName] ?? f.defaultValue ?? '';
          // Handle TODAY default for date fields
          if (val === 'TODAY' && (f.fieldType === 'DATE' || f.fieldType === 'DATETIME')) {
            val = new Date().toISOString().split('T')[0];
          }
          if (f.fieldType === 'DATETIME') val = toDateTimeInput(val);
          initial[f.fieldName] = val;
        });
        // The fields are on screen before this point (setHeaderFields above), so anything the user
        // already typed must survive: starting values only fill fields they haven't touched (FH-197).
        setHeaderValues((prev) => {
          const merged = { ...initial };
          touchedHeaderFieldsRef.current.forEach((name) => {
            if (prev[name] !== undefined) merged[name] = prev[name];
          });
          return merged;
        });

        // Fetch detail entity fields if provided
        if (detailEntityName) {
          const lFields = await fetchFields(detailEntityName);
          setLineFields(lFields);
          // Start with one empty line
          if (!isEdit) {
            setLines([createEmptyLine(lFields)]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch wizard metadata:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMeta();
  }, [entityName, detailEntityName, rowData, isEdit, fieldsVersion]);

  // Resolve $ref picker configs for PICKER fields
  useEffect(() => {
    if (headerFields.length === 0) return;
    const pickerFields = headerFields.filter((f) => f.fieldType === 'PICKER' && f.allowedValues);
    if (pickerFields.length === 0) return;

    const resolveAll = async () => {
      const resolved = { ...resolvedPickers };
      for (const f of pickerFields) {
        try {
          const config = typeof f.allowedValues === 'string' ? JSON.parse(f.allowedValues) : f.allowedValues;
          if (config && config['$ref'] && !resolved[config['$ref']]) {
            // Fetch ui_component by code (scope-aware)
            const token = localStorage.getItem('waide_access_token');
            const resp = await fetch(`/api/v1/ui-components/resolve/${config['$ref']}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (resp.ok) {
              const comp = await resp.json();
              if (comp && comp.definition) {
                const def = typeof comp.definition === 'string' ? JSON.parse(comp.definition) : comp.definition;
                // Merge $overrides if present
                if (config['$overrides']) Object.assign(def, config['$overrides']);
                resolved[config['$ref']] = def;
              }
            }
          } else if (config && !config['$ref']) {
            // Inline picker config (no $ref) — use as-is
            resolved[f.fieldName] = config;
          }
        } catch (e) {
          console.error('Failed to resolve picker config for', f.fieldName, e);
        }
      }
      setResolvedPickers(resolved);
    };
    resolveAll();
  }, [headerFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load dynamic options for DROPDOWN/AUTOCOMPLETE fields with optionsScript.
  // Passes rowData as _payload so row-aware option scripts (e.g.
  // suggest_putaway_locations) can read itemId/palletId/etc. We intentionally
  // use rowData (a stable prop) rather than headerValues (state that may not
  // be populated yet — fetchMeta has an await between setHeaderFields and
  // setHeaderValues, so the headerFields-triggered effect can fire first).
  useEffect(() => {
    if (headerFields.length === 0) return;
    const scriptsToLoad = headerFields.filter(
      (f) => (f.fieldType === 'DROPDOWN' || f.fieldType === 'CHOICE_BUTTONS' || f.fieldType === 'AUTOCOMPLETE' || f.fieldType === 'MULTICOMBOBOX' || f.fieldType === 'MULTISELECT' || f.fieldType === 'CHECKBOX_GROUP') && f.optionsScript
    );
    if (scriptsToLoad.length === 0) return;

    const loadAll = async () => {
      const results = {};
      const payload = rowData || {};
      const seqAtStart = {};
      scriptsToLoad.forEach((f) => {
        seqAtStart[f.fieldName] = (optionsSeqRef.current[f.fieldName] || 0) + 1;
        optionsSeqRef.current[f.fieldName] = seqAtStart[f.fieldName];
      });
      await Promise.all(
        scriptsToLoad.map(async (f) => {
          try {
            const response = await FlexService.executeScript({
              scriptName: f.optionsScript,
              // _field/_entity: which field the options are for (custom_field_options needs it)
              context: { _payload: { ...payload, _field: f.fieldName, _entity: entityName } },
            });
            if (response.success) {
              const ctx = response.context || {};
              const opts = ctx.result || ctx.data || [];
              if (Array.isArray(opts)) {
                results[f.fieldName] = opts;
              }
            }
          } catch (err) {
            console.error(`Failed to load options from script "${f.optionsScript}" for field "${f.fieldName}":`, err);
          }
        })
      );
      // keep only fields nobody has searched since this load started
      Object.keys(results).forEach((name) => {
        if (optionsSeqRef.current[name] !== seqAtStart[name]) delete results[name];
      });
      if (Object.keys(results).length > 0) {
        setDynamicOptions((prev) => ({ ...prev, ...results }));
      }
    };
    loadAll();
  }, [headerFields, rowData, entityName]);

  // A dropdown whose choices depend on other fields of the form (optionsDependOn, e.g. an order line's unit on its
  // item, FH-309) asks for them again whenever those fields change. A chosen value no longer offered gives way to the
  // first choice offered (the item's own unit).
  const dependentDropdowns = headerFields.filter((f) => f.fieldType === 'DROPDOWN' && f.optionsScript && f.optionsDependOn);
  const dependentKey = dependentDropdowns
    .map((f) => `${f.fieldName}:${f.optionsDependOn.split(',').map((d) => headerValues?.[d.trim()] ?? '').join('|')}`)
    .join(';');
  useEffect(() => {
    if (dependentDropdowns.length === 0) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const results = {};
      await Promise.all(dependentDropdowns.map(async (f) => {
        try {
          const response = await FlexService.executeScript({
            scriptName: f.optionsScript,
            context: { _payload: { ...(rowData || {}), ...(headerValues || {}), _field: f.fieldName, _entity: entityName } },
          });
          const opts = response?.context?.result || response?.context?.data || [];
          if (response?.success && Array.isArray(opts)) results[f.fieldName] = opts;
        } catch (err) {
          console.error(`Failed to load options from script "${f.optionsScript}" for field "${f.fieldName}":`, err);
        }
      }));
      if (cancelled || Object.keys(results).length === 0) return;
      setDynamicOptions((prev) => ({ ...prev, ...results }));
      setHeaderValues((prev) => {
        const next = { ...prev };
        Object.entries(results).forEach(([name, opts]) => {
          if (opts.length > 0 && !opts.some((o) => String(o.value) === String(next[name]))) next[name] = opts[0].value;
        });
        return next;
      });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentKey, headerFields.length]);

  // A line under a field written by its hintScript from the form's current values (e.g. the summary of an item's
  // units and packs, FH-316), asked again a moment after the values change
  const [fieldHints, setFieldHints] = useState({});
  const hintedFields = headerFields.filter((f) => f.hintScript);
  const hintKey = hintedFields.length ? JSON.stringify(headerValues) : '';
  useEffect(() => {
    if (hintedFields.length === 0) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const hints = {};
      await Promise.all(hintedFields.map(async (f) => {
        try {
          const response = await FlexService.executeScript({
            scriptName: f.hintScript,
            context: { _payload: { ...(rowData || {}), ...(headerValues || {}), _field: f.fieldName, _entity: entityName } },
          });
          const text = response?.context?.result;
          hints[f.fieldName] = response?.success && typeof text === 'string' ? text : '';
        } catch (err) {
          console.error(`Failed to run hint script "${f.hintScript}" for field "${f.fieldName}":`, err);
        }
      }));
      if (!cancelled) setFieldHints((prev) => ({ ...prev, ...hints }));
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintKey, headerFields.length]);

  // Conditional (computed) defaults via defaultValueScript -- same plumbing as
  // optionsScript (FlexService.executeScript, same _payload convention) but
  // returns a single scalar via `result` instead of a list of {value,label}.
  // Re-runs (debounced) whenever any header value changes, so a field like
  // "Facility Type" can drive sensible defaults for warehouseCode/warehouseName
  // -- but only ever fills a field that is currently empty AND has not been
  // manually edited by the user (touchedHeaderFieldsRef), so it never clobbers
  // real input. Ported from RapidEntryOverlay's identical line-level mechanism
  // (FH-129) -- this single-entity CREATE form never had it wired up before.
  useEffect(() => {
    if (headerFields.length === 0) return;
    const scriptedFields = headerFields.filter((f) => f.defaultValueScript);
    if (scriptedFields.length === 0) return;

    if (defaultScriptDebounceRef.current) clearTimeout(defaultScriptDebounceRef.current);
    defaultScriptDebounceRef.current = setTimeout(async () => {
      // the row too, as options and hints get it: a form opened from a row works out its starting values from it
      const payload = { ...(rowData || {}), ...headerValues };
      const results = {};
      await Promise.all(
        scriptedFields.map(async (f) => {
          // Never recompute/overwrite a field the user already filled in or
          // manually edited -- a default is a convenience, not a correction.
          if (touchedHeaderFieldsRef.current.has(f.fieldName)) return;
          const current = headerValues[f.fieldName];
          // an empty checkbox group ([]) is empty too, so its default ticks still apply (FH-339)
          if (current !== undefined && current !== null && current !== '' && !(Array.isArray(current) && current.length === 0)) return;
          try {
            const response = await FlexService.executeScript({
              scriptName: f.defaultValueScript,
              context: { _payload: payload },
            });
            if (response.success) {
              const val = response.context?.result;
              if (val !== undefined && val !== null && val !== '') {
                results[f.fieldName] = val;
              }
            }
          } catch (err) {
            console.error(`Failed to compute default from script "${f.defaultValueScript}" for field "${f.fieldName}":`, err);
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setHeaderValues((prev) => {
          // Re-check emptiness/touched against the LATEST state, not the
          // stale closure snapshot -- the user may have typed into one of
          // these fields while the script calls above were in flight.
          const merged = { ...prev };
          Object.entries(results).forEach(([fieldName, val]) => {
            if (touchedHeaderFieldsRef.current.has(fieldName)) return;
            const cur = merged[fieldName];
            if (cur === undefined || cur === null || cur === '' || (Array.isArray(cur) && cur.length === 0)) {
              merged[fieldName] = val;
            }
          });
          return merged;
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 300);
    return () => {
      if (defaultScriptDebounceRef.current) clearTimeout(defaultScriptDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerFields, headerValues]);

  const createEmptyLine = (fields) => {
    const line = { _lineId: Date.now() + Math.random() };
    (fields || lineFields).forEach((f) => {
      let val = f.defaultValue ?? '';
      if (val === 'TODAY' && (f.fieldType === 'DATE' || f.fieldType === 'DATETIME')) {
        val = new Date().toISOString().split('T')[0];
      }
      line[f.fieldName] = val;
    });
    return line;
  };

  const handleHeaderChange = useCallback((fieldName, value) => {
    touchedHeaderFieldsRef.current.add(fieldName);
    setHeaderValues((prev) => ({ ...prev, [fieldName]: value }));
    // Only clear required-field errors on change; async validation errors stay until re-validated on blur
    setErrors((prev) => {
      const field = headerFields.find((f) => f.fieldName === fieldName);
      if (field && field.validationScript) return prev; // keep async validation error until blur re-validates
      const n = { ...prev }; delete n[fieldName]; return n;
    });
    setSaveError(null);

    // Load dependent fields when a field with dependentFieldsScript changes
    const field = headerFields.find((f) => f.fieldName === fieldName);
    if (field?.dependentFieldsScript && value) {
      setDependentFieldsLoading(true);
      FlexService.executeScript({
        scriptName: field.dependentFieldsScript,
        context: { _payload: { value, fieldName } },
      }).then((res) => {
        const raw = res?.context?.result || [];
        // Normalize to a consistent field definition shape — one mapping, one contract
        const normalized = raw.map((r) => ({
          fieldName: r.fieldName || r.parameterKey || r.parameter_key,
          displayLabel: r.displayLabel || r.display_label || r.fieldName || r.parameterKey || '',
          fieldType: (() => { const dt = r.fieldType || r.dataType || r.data_type || 'TEXT'; return dt === 'NUMBER' ? 'NUMBER' : dt === 'ENTITY_PICKER' ? 'PICKER' : 'TEXT'; })(),
          isRequired: r.isRequired ?? r.is_required ?? false,
          defaultValue: r.defaultValue ?? r.default_value,
          minValue: r.minValue ?? r.min_value,
          maxValue: r.maxValue ?? r.max_value,
          displayOrder: r.displayOrder ?? r.sortOrder ?? r.sort_order ?? 0,
          description: r.displayLabel || r.display_label || '',
          entityName: r.entityName ?? r.entity_name,
        }));
        setDependentFields(normalized);
        // Seed default values into headerValues so they're captured on submit
        const defaults = {};
        normalized.forEach((df) => {
          if (df.defaultValue != null && df.defaultValue !== '') {
            defaults[df.fieldName] = df.defaultValue;
          }
        });
        if (Object.keys(defaults).length > 0) {
          setHeaderValues((prev) => ({ ...defaults, ...prev }));
        }
        setDependentFieldsLoading(false);
      }).catch(() => {
        setDependentFields([]);
        setDependentFieldsLoading(false);
      });
    } else if (field?.dependentFieldsScript && !value) {
      setDependentFields([]);
    }
  }, [headerFields]);

  const handleLineChange = useCallback((lineIndex, fieldName, value) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[lineIndex] = { ...updated[lineIndex], [fieldName]: value };
      return updated;
    });
    setLineErrors((prev) => {
      const key = `${lineIndex}.${fieldName}`;
      const n = { ...prev };
      delete n[key];
      return n;
    });
    setSaveError(null);
  }, []);

  // Async field validation via FLEX script (called on blur)
  const handleFieldBlur = useCallback(async (field, value, errorKey) => {
    console.log('[EntityWizardOverlay] Field blur:', field.fieldName, 'validationScript:', field.validationScript, 'value:', value);
    if (!field.validationScript || !value || value === '') return;
    if (field.isEditable === false && isEdit) return; // read-only field on edit -- see handleSave's matching guard
    const vKey = errorKey || field.fieldName;
    setValidating((prev) => ({ ...prev, [vKey]: true }));
    try {
      const response = await FlexService.executeScript({
        scriptName: field.validationScript,
        context: {
          _payload: { [field.fieldName]: value }, fieldValue: value, fieldName: field.fieldName,
          formValues: errorKey
            ? { ...(formValuesRef.current.lines[Number(String(errorKey).split('.')[0])] || {}), [field.fieldName]: value }
            : { ...formValuesRef.current.headerValues, [field.fieldName]: value },
        },
      });
      // Check both engine-level success and script-level success variable
      const scriptSuccess = response.context?.success;
      if (!response.success || scriptSuccess === false) {
        const msg = response.context?.error_message || response.errorMessage || `Invalid ${field.displayLabel}`;
        if (errorKey) {
          setLineErrors((prev) => ({ ...prev, [vKey]: msg }));
        } else {
          setErrors((prev) => ({ ...prev, [vKey]: msg }));
        }
      } else {
        // Validation passed — clear any previous error on this field
        if (errorKey) {
          setLineErrors((prev) => { const n = { ...prev }; delete n[vKey]; return n; });
        } else {
          setErrors((prev) => { const n = { ...prev }; delete n[vKey]; return n; });
        }

        // Check for item/entity attribute data in response — dynamically update field requirements
        const ctx = response.context || {};
        const entityData = ctx.itemData || ctx.entityData || ctx.validationData;
        if (entityData) {
          const firstItem = Array.isArray(entityData) ? entityData[0] : entityData;
          if (firstItem) {
            const overrides = {};
            // If item is lot tracked, make lotCode required
            if (firstItem.isLotTracked === true || firstItem.isLotTracked === 'true') {
              overrides.lotCode = { isRequired: true, description: 'Required — item is lot tracked' };
            } else {
              overrides.lotCode = { isRequired: false, description: 'Lot or batch code' };
            }
            // If item tracks expiration, make expirationDate required
            if (firstItem.isExpired === true || firstItem.isExpired === 'true' ||
                firstItem.isExpirationTracked === true || firstItem.isExpirationTracked === 'true') {
              overrides.expirationDate = { isRequired: true, description: 'Required — item tracks expiration' };
            } else {
              overrides.expirationDate = { isRequired: false, description: 'Expiration date' };
            }
            setDynamicFieldOverrides((prev) => ({ ...prev, ...overrides }));

            // Auto-populate related fields from validation response
            const autoFills = {};
            if (firstItem.itemDescription !== undefined) autoFills.itemDescription = firstItem.itemDescription;
            if (firstItem.description !== undefined) autoFills.itemDescription = firstItem.description;
            if (firstItem.unitCost !== undefined) autoFills.unitPrice = firstItem.unitCost;
            if (firstItem.unitPrice !== undefined) autoFills.unitPrice = firstItem.unitPrice;
            if (firstItem.lowestUom !== undefined && !headerValues.orderedUom) autoFills.orderedUom = firstItem.lowestUom;

            if (Object.keys(autoFills).length > 0) {
              if (errorKey) {
                // Line field — update the specific line
                const lineIdx = parseInt(errorKey.split('-')[0], 10);
                if (!isNaN(lineIdx)) {
                  setLines((prev) => prev.map((line, i) =>
                    i === lineIdx ? { ...line, ...autoFills } : line
                  ));
                }
              } else {
                // Header field
                setHeaderValues((prev) => ({ ...prev, ...autoFills }));
              }
            }
          }
        }
      }
    } catch (err) {
      console.error(`Validation failed for ${field.fieldName}:`, err);
    } finally {
      setValidating((prev) => ({ ...prev, [vKey]: false }));
    }
  }, []);

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, createEmptyLine()]);
  }, [lineFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeLine = useCallback((index) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};
    headerFields.forEach((f) => {
      if (!isFieldShownFor(f, headerValues)) return;   // a hidden question is not asked, so not required
      // Apply dynamic overrides (e.g., lotCode required when item is lot-tracked)
      const override = dynamicFieldOverrides[f.fieldName];
      const isRequired = override?.isRequired ?? f.isRequired;
      // isEditable:false means "can't change after creation" (record codes such as Location Code):
      // still typed in, and required, on a create form (FH-197).
      if (isRequired && (f.isEditable !== false || !isEdit)) {
        const val = headerValues[f.fieldName];
        if (val === undefined || val === null || val === '') {
          newErrors[f.fieldName] = `${f.displayLabel} is required`;
        }
      }
    });

    // Validate dependent fields (dynamic parameters)
    dependentFields.forEach((df) => {
      if (df.isRequired) {
        const val = headerValues[df.fieldName];
        if (val === undefined || val === null || val === '') {
          newErrors[df.fieldName] = `${df.displayLabel} is required`;
        }
      }
    });

    const newLineErrors = {};
    if (detailEntityName && lines.length > 0) {
      lines.forEach((line, idx) => {
        lineFields.forEach((f) => {
          if (f.isRequired && f.isEditable !== false) {
            const val = line[f.fieldName];
            if (val === undefined || val === null || val === '') {
              newLineErrors[`${idx}.${f.fieldName}`] = `${f.displayLabel} is required`;
            }
          }
        });
      });
    }

    setErrors(newErrors);
    setLineErrors(newLineErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(newLineErrors).length === 0;
  }, [headerFields, headerValues, detailEntityName, lines, lineFields, dependentFields, dynamicFieldOverrides, isEdit]);

  const handleSave = useCallback(async () => {
    if (!validate()) {
      console.warn('[EntityWizardOverlay] Validation failed — save blocked', { errors, lineErrors: lineErrors });
      return;
    }

    // Re-run all async field validations before saving
    const asyncErrors = {};
    const asyncLineErrors = {};
    const asyncValidations = [];

    headerFields.forEach((f) => {
      // Skip re-validating a field the user can't actually change in this mode
      // (e.g. a uniqueness check on a primary-key-like field such as Area.areaId
      // that's read-only on edit) -- its value was already validated when the
      // record was created, and re-checking it here just matches the record
      // against itself and produces a false "already exists" error.
      const isFieldDisabled = f.isEditable === false && isEdit;
      if (f.validationScript && headerValues[f.fieldName] && !isFieldDisabled) {
        asyncValidations.push(
          FlexService.executeScript({
            scriptName: f.validationScript,
            context: { _payload: { [f.fieldName]: headerValues[f.fieldName] }, fieldValue: headerValues[f.fieldName], fieldName: f.fieldName, formValues: headerValues },
          }).then((response) => {
            const scriptSuccess = response.context?.success;
            if (!response.success || scriptSuccess === false) {
              asyncErrors[f.fieldName] = response.context?.error_message || response.errorMessage || `Invalid ${f.displayLabel}`;
            }
          }).catch(() => {})
        );
      }
    });

    if (detailEntityName && lines.length > 0) {
      lines.forEach((line, idx) => {
        lineFields.forEach((f) => {
          if (f.validationScript && line[f.fieldName] && f.isEditable !== false) {
            const errKey = `${idx}.${f.fieldName}`;
            asyncValidations.push(
              FlexService.executeScript({
                scriptName: f.validationScript,
                context: { _payload: { [f.fieldName]: line[f.fieldName] }, fieldValue: line[f.fieldName], fieldName: f.fieldName, formValues: line },
              }).then((response) => {
                const scriptSuccess = response.context?.success;
                if (!response.success || scriptSuccess === false) {
                  asyncLineErrors[errKey] = response.context?.error_message || response.errorMessage || `Invalid ${f.displayLabel}`;
                }
              }).catch(() => {})
            );
          }
        });
      });
    }

    if (asyncValidations.length > 0) {
      await Promise.all(asyncValidations);
      if (Object.keys(asyncErrors).length > 0 || Object.keys(asyncLineErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...asyncErrors }));
        setLineErrors((prev) => ({ ...prev, ...asyncLineErrors }));
        setSaveError('Please fix the validation errors before saving.');
        return;
      }
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Build the payload using wms_payload_path mappings from field metadata
      // Start with hidden field defaults (e.g. isAutoGeneratedId=true)
      const payload = { ...hiddenFieldDefaults };
      if (rowData) {
        Object.keys(rowData).forEach((key) => {
          if (rowData[key] !== undefined && rowData[key] !== null && key !== '_lineId') {
            payload[key] = rowData[key];
          }
        });
      }

      // Map header fields (overrides rowData values with form values)
      headerFields.forEach((f) => {
        // a hidden field is saved empty: its answer no longer applies (FH-316)
        let val = isFieldShownFor(f, headerValues) ? headerValues[f.fieldName] : '';
        let path = f.wmsPayloadPath || f.fieldName;
        // When a line entity is used as a header form (single-line edit, e.g.
        // Receipts → Edit Receipt Line), the field's wmsPayloadPath still carries
        // the batch-array prefix like "receiptLine[].expectedQuantity". The line
        // mapping below strips this before placing into the lines array — header
        // mode needs to do the same so the value lands on the flat field name
        // that the WMS update endpoint actually reads.
        const bracketIdx = path.indexOf('[].');
        if (bracketIdx >= 0) {
          path = path.substring(bracketIdx + 3);
        }

        if (isEdit || embedded) {
          // Edit/embedded mode: always send the current form value to allow clearing
          if (val !== undefined) {
            if (f.fieldType === 'DATE' && typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
              val = val + 'T00:00:00Z';
            }
            if (f.fieldType === 'DATETIME') val = fromDateTimeInput(val);
            // Send type-appropriate empty values (not null — WMS skips null fields)
            if (val === '' || val === null) {
              if (f.fieldType === 'NUMBER' || f.fieldType === 'DECIMAL') {
                // FH-173/174: a blank number is never 0 (blank usually means "not set" or
                // "no limit") and never "" (WMS number parsing throws on it). Still blank
                // since it was loaded: leave it out. Cleared by the user: null.
                const original = rowData ? rowData[f.fieldName] : undefined;
                if (original === undefined || original === null || original === '') {
                  delete payload[path];
                  return;
                }
                val = null;
              } else if (f.fieldType === 'BOOLEAN') val = false;
              else val = '';
            }
            payload[path] = val;
          }
        } else {
          // Create mode: skip truly empty values
          if (val !== undefined && val !== null && val !== '') {
            if (f.fieldType === 'DATE' && typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
              val = val + 'T00:00:00Z';
            }
            if (f.fieldType === 'DATETIME') val = fromDateTimeInput(val);
            payload[path] = val;
          }
        }
      });

      // Include dependent field values (dynamic parameters like scenario params)
      dependentFields.forEach((df) => {
        const val = headerValues[df.fieldName];
        if (val !== undefined && val !== null && val !== '') {
          payload[df.fieldName] = val;
        }
      });

      // Map line fields into detail array (key derived from wmsPayloadPath prefix, e.g. "receiptLine[].itemId" → "receiptLine")
      if (detailEntityName && lines.length > 0) {
        // Derive the array key from the first line field's wmsPayloadPath prefix
        let detailArrayKey = 'lines'; // default
        const firstPathField = lineFields.find((f) => f.wmsPayloadPath && f.wmsPayloadPath.includes('[].'));
        if (firstPathField) {
          detailArrayKey = firstPathField.wmsPayloadPath.split('[].')[0];
        }

        const mappedLines = lines.map((line) => {
          const mapped = {};
          lineFields.forEach((f) => {
            let val = line[f.fieldName];
            if (val !== undefined && val !== null && val !== '') {
              // Convert plain date (yyyy-MM-dd) to ISO 8601 for WMS API
              if (f.fieldType === 'DATE' && typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
                val = val + 'T00:00:00Z';
              }
              // Extract the actual field name from wmsPayloadPath like "receiptLine[].itemId"
              let path = f.wmsPayloadPath || f.fieldName;
              const bracketIdx = path.indexOf('[].');
              if (bracketIdx >= 0) {
                path = path.substring(bracketIdx + 3);
              }
              mapped[path] = val;
            }
          });
          return mapped;
        });
        payload[detailArrayKey] = mappedLines;
      }

      foldCustomFields(payload); // custom field values go to the WMS as one map (FH-202)

      // Execute via FLEX script — a named script is required for all WMS interactions
      console.log('[EntityWizardOverlay] Save payload:', JSON.stringify(payload, null, 2), '| scriptName:', scriptName);
      if (!scriptName) {
        setSaveError(`No FLEX script configured for this action. A script_name must be defined in entity_action_metadata for ${entityName}.`);
        setSaving(false);
        return;
      }
      const response = await FlexService.executeScript({
        scriptName,
        context: { _payload: buildPayload ? buildPayload(payload) : payload },
      });

      // response.success is ENGINE-level only (did the script run without throwing) --
      // it's true even when the script's own success/error_message convention (used
      // by area_update.flex, area_setup.flex, and most WMS-write scripts) reports a
      // real business-logic failure, e.g. a 400 rejection from WMS. That convention
      // never throws on a validation failure -- it's expected business logic, not an
      // engine error -- so this check was previously the ONLY one in this function,
      // meaning any such failure silently saved as if it succeeded (FH-56, 2026-10-05:
      // found via "Area saves without blocking when Pickable has no Pick Level
      // selected," even after the server-side validation was added and confirmed
      // working via direct API calls -- the UI just never looked at the result).
      // The same script-level check already exists elsewhere in this file for
      // field-level async validation (see validateFieldAsync above) -- this mirrors it.
      const scriptSuccess = response.context?.success;
      if (response.success && scriptSuccess !== false) {
        onSave && onSave(response.context?.result || response);
      } else {
        const diag = response.diagnostics?.[0];
        // a Command can name the fields its message is about: said under each field, not again at the top
        const byField = response.context?.result?.fieldErrors;
        if (byField && typeof byField === 'object' && Object.keys(byField).length) {
          setErrors((prev) => ({ ...prev, ...byField }));
          setSaveError(null);
        } else {
          setSaveError(response.context?.error_message || diag?.message || response.errorMessage || 'Failed to save. Please try again.');
        }
      }
    } catch (err) {
      console.error('Wizard save failed:', err);
      setSaveError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [validate, headerFields, headerValues, detailEntityName, lines, lineFields, onSave, scriptName, entityName, buildPayload]);

  const renderField = (field, value, onChange, error, isLine = false, onBlur = null, isValidating = false) => {
    // Generic cross-field gate (e.g. Area.pickedLevels only makes sense when
    // isPickable is checked -- FH-56, 2026-10-05). Header-level only: line
    // items don't carry this dependency today. Disabling (rather than hiding)
    // the field when its dependency isn't met keeps the field visible so the
    // user understands why it's inert, and preserves whatever value it already
    // has instead of discarding it.
    const dependencyUnmet = !isLine && field.enabledWhenField
      && String(headerValues[field.enabledWhenField]) !== String(field.enabledWhenValue ?? 'true');
    const isDisabled = (field.isEditable === false && isEdit) || dependencyUnmet;
    const blurHandler = onBlur ? () => onBlur(field, value) : undefined;

    // Helper to apply text transform (UPPERCASE, LOWERCASE, CAPITALIZE)
    const applyTransform = (val) => {
      if (!val || !field.textTransform) return val;
      if (field.textTransform === 'UPPERCASE') return val.toUpperCase();
      if (field.textTransform === 'LOWERCASE') return val.toLowerCase();
      return val;
    };
    const handleChange = (fieldName, val) => onChange(fieldName, applyTransform(val));

    const commonSx = {
      mb: isLine ? 0 : 2,
      '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        bgcolor: isDisabled ? colors.surfaceElevated : colors.background,
        '& fieldset': { borderColor: error ? '#ef4444' : colors.border },
        '&:hover fieldset': { borderColor: isDisabled ? colors.border : '#22c55e' },
        '&.Mui-focused fieldset': { borderColor: '#22c55e' },
      },
      '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: isLine ? '0.8rem' : '0.875rem' },
      '& .MuiInputLabel-root': { color: colors.textSecondary },
    };

    // Check field renderer registry for custom field types first
    const customRenderer = fieldRendererRegistry.resolve(field.fieldType);
    if (customRenderer) {
      return customRenderer({
        field, value, onChange, onBlur: blurHandler, error,
        isLine, isEdit, isDisabled, isValidating,
        colors, commonSx, dynamicOptions, resolvedPickers,
        setPickerField, setPickerOpen, FlexService, handleChange,
      });
    }

    switch (field.fieldType) {
      case 'SCAN': {
        // Barcode-friendly text input. Accepts manual typing too. Auto-focus on mount.
        // Submits the form on Enter when used in a rapid-entry context — the parent
        // form catches the Enter via its onKeyDown handler. We just behave like TEXT
        // with autoFocus and a hint placeholder.
        return (
          <TextField
            fullWidth size="small"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            placeholder={isLine ? (field.placeholder || 'Scan or leave blank') : (field.placeholder || 'Scan or leave blank')}
            value={value || ''}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            onBlur={blurHandler}
            disabled={isDisabled}
            error={!!error}
            helperText={isValidating ? 'Validating...' : (error || (!isLine && field.description))}
            autoFocus
            inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false }}
            sx={{
              ...commonSx,
              '& .MuiInputBase-input': { ...commonSx['& .MuiInputBase-input'], fontFamily: 'monospace' },
            }}
          />
        );
      }
      case 'MULTICOMBOBOX':
        return (
          <MultiComboboxField
            key={field.fieldName}
            field={field}
            value={value}
            options={dynamicOptions[field.fieldName] || []}
            onChange={onChange}
            disabled={isDisabled}
            error={error}
            colors={colors}
          />
        );

      case 'CHOICE_BUTTONS': {
        // A few choices shown side by side, one press to choose (an inspection check's Pass / Conditional / Fail).
        // Options as for DROPDOWN; an option's color (success, warning, error) colors its button.
        const staticOptions = field.allowedValues
          ? (typeof field.allowedValues === 'string' ? JSON.parse(field.allowedValues) : field.allowedValues)
          : [];
        const options = (field.optionsScript && dynamicOptions?.[field.fieldName]) ? dynamicOptions[field.fieldName] : staticOptions;
        return (
          <FormControl fullWidth required={!!field.isRequired} sx={{ mb: isLine ? 0 : 2 }}>
            <FormLabel sx={{ color: colors.textSecondary, mb: 0.5, fontSize: '0.875rem' }}>{field.displayLabel}</FormLabel>
            <ToggleButtonGroup exclusive value={value || null} aria-label={field.displayLabel} disabled={isDisabled}
              onChange={(_, v) => { if (v !== null) onChange(field.fieldName, v); }}>
              {options.map((opt) => {
                const optValue = typeof opt === 'object' ? (opt.value ?? opt.optionValue ?? opt.code) : opt;
                const optLabel = typeof opt === 'object' ? (opt.label || opt.description || optValue) : opt;
                return (
                  <ToggleButton key={optValue} value={optValue} color={(typeof opt === 'object' && opt.color) || 'primary'} sx={{ borderRadius: 0, px: 3 }}>
                    {optLabel}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {field.description && !error && <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>}
          </FormControl>
        );
      }

      case 'DROPDOWN': {
        // Options: static allowedValues OR dynamic from optionsScript (loaded into dynamicOptions cache)
        const staticOptions = field.allowedValues
          ? (typeof field.allowedValues === 'string' ? JSON.parse(field.allowedValues) : field.allowedValues)
          : [];
        const options = (field.optionsScript && dynamicOptions?.[field.fieldName])
          ? dynamicOptions[field.fieldName]
          : staticOptions;
        // MUI only wires Select's aria-labelledby to the InputLabel when
        // labelId/id are explicitly linked -- without this, the Select has no
        // accessible name at all (confirmed live via a Playwright a11y
        // snapshot: the Direction field's combobox had an empty name, only
        // matchable by its current displayed value, not "Direction").
        const dropdownLabelId = `${field.fieldName}-select-label`;
        return (
          <FormControl fullWidth size="small" required={!isLine && !!field.isRequired} sx={{ ...commonSx }}>
            {!isLine && <InputLabel id={dropdownLabelId}>{field.displayLabel}</InputLabel>}
            <Select
              labelId={isLine ? undefined : dropdownLabelId}
              value={value || ''}
              onChange={(e) => onChange(field.fieldName, e.target.value)}
              label={isLine ? undefined : field.displayLabel}
              disabled={isDisabled}
              displayEmpty
              sx={{ color: colors.textPrimary, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border, borderRadius: 0 } }}
            >
              {isLine && <MenuItem value="" disabled><em>{field.placeholder || field.displayLabel}</em></MenuItem>}
              {options.map((opt) => {
                const fn = field.fieldName;
                const nameKey = fn.replace(/Id$/, 'Name');
                const optValue = typeof opt === 'object' ? (opt.value || opt.optionValue || opt.code || opt[fn] || opt.name || opt.key || Object.values(opt)[0]) : opt;
                const optLabel = typeof opt === 'object' ? (opt.label || opt.description || opt.displayName || opt[nameKey] || opt.name || opt.display_name || opt.value || opt.optionValue || optValue) : opt;
                return <MenuItem key={optValue} value={optValue}>{optLabel}</MenuItem>;
              })}
            </Select>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {!isLine && field.description && !error && (
              <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>
            )}
          </FormControl>
        );
      }

      case 'MULTISELECT': {
        // Options: static allowedValues OR dynamic from optionsScript -- same
        // precedence/shape as the DROPDOWN case above.
        const staticOptions = field.allowedValues
          ? (typeof field.allowedValues === 'string' ? JSON.parse(field.allowedValues) : field.allowedValues)
          : [];
        const options = (field.optionsScript && dynamicOptions?.[field.fieldName])
          ? dynamicOptions[field.fieldName]
          : staticOptions;
        const selected = Array.isArray(value) ? value : [];
        // Nothing to choose from (e.g. Owners in a warehouse with no owners): an optional
        // field whose option list loaded empty, with nothing selected, is not shown.
        if (field.optionsScript && Array.isArray(dynamicOptions?.[field.fieldName])
            && options.length === 0 && selected.length === 0 && !field.isRequired) {
          return null;
        }
        const multiselectLabelId = `${field.fieldName}-select-label`;
        return (
          <FormControl fullWidth size="small" required={!isLine && !!field.isRequired} sx={{ ...commonSx }}>
            {!isLine && <InputLabel id={multiselectLabelId}>{field.displayLabel}</InputLabel>}
            <Select
              labelId={isLine ? undefined : multiselectLabelId}
              multiple
              value={selected}
              onChange={(e) => onChange(field.fieldName, e.target.value)}
              label={isLine ? undefined : field.displayLabel}
              disabled={isDisabled}
              renderValue={(sel) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sel.map((v) => {
                    const opt = options.find((o) => (typeof o === 'object' ? o.value : o) === v);
                    const optLabel = typeof opt === 'object' ? opt.label : (opt ?? v);
                    return <Chip key={v} label={optLabel} size="small" sx={{ borderRadius: 0 }} />;
                  })}
                </Box>
              )}
              sx={{ color: colors.textPrimary, '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border, borderRadius: 0 } }}
            >
              {options.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <MenuItem key={optValue} value={optValue}>
                    <Checkbox checked={selected.includes(optValue)} size="small" />
                    {optLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {field.description && !error && (
              <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>
            )}
          </FormControl>
        );
      }

      case 'CHECKBOX_GROUP': {
        // Same multi-value semantics as MULTISELECT, but flattened into inline
        // checkboxes instead of a dropdown-with-chips -- for a short, fixed
        // option list (e.g. Area.pickedLevels: Each/Case/Pallet) where opening
        // a dropdown is an extra click for no benefit (FH-56, 2026-10-05,
        // requested directly: "may be needs a check box flattened for the
        // user to select").
        const staticOptions = field.allowedValues
          ? (typeof field.allowedValues === 'string' ? JSON.parse(field.allowedValues) : field.allowedValues)
          : [];
        const options = (field.optionsScript && dynamicOptions?.[field.fieldName])
          ? dynamicOptions[field.fieldName]
          : staticOptions;
        const selected = Array.isArray(value) ? value : [];
        const toggle = (optValue) => {
          const next = selected.includes(optValue)
            ? selected.filter((v) => v !== optValue)
            : [...selected, optValue];
          onChange(field.fieldName, next);
        };
        return (
          <Box sx={{ mb: isLine ? 0 : 2 }}>
            {!isLine && (
              <FormLabel required={!!field.isRequired} sx={{ fontSize: '0.8rem', color: colors.textSecondary, display: 'block', mb: 0.5 }}>
                {field.displayLabel}
              </FormLabel>
            )}
            <FormGroup row>
              {options.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <FormControlLabel
                    key={optValue}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(optValue)}
                        onChange={() => toggle(optValue)}
                        disabled={isDisabled}
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: colors.textPrimary }}>{optLabel}</Typography>}
                  />
                );
              })}
            </FormGroup>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {field.description && !error && (
              <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>
            )}
          </Box>
        );
      }

      case 'AUTOCOMPLETE': {
        // freeSolo Autocomplete — user picks a script-provided suggestion or types their own.
        // Input field shows just the value (e.g. locationId); dropdown shows the
        // richer label (e.g. "B2-04-C (score -0.04)") so users see the score
        // when picking but it doesn't end up in the field.
        const allOpts = (field.optionsScript && dynamicOptions?.[field.fieldName]) || [];
        // An option marked info is not a choice: it explains why there is nothing to
        // suggest (e.g. no area accepts this stock) and is shown under the field.
        const infoMessage = allOpts.find((o) => o && typeof o === 'object' && o.info)?.label || null;
        const opts = allOpts.filter((o) => !(o && typeof o === 'object' && o.info));
        const suggestedCount = opts.filter((o) => o && typeof o === 'object' && o.suggested).length;
        // When options carry a numeric score (AI suggestions), normalize it to
        // a 0-100% match against the candidate set — friendlier than raw
        // Q-values that can be negative or compressed in a small range. The
        // best candidate always shows 100%, the worst 0%.
        const rawScores = opts
          .map((o) => (typeof o === 'object' && typeof o.score === 'number' ? o.score : null))
          .filter((s) => s != null);
        const sMin = rawScores.length ? Math.min(...rawScores) : 0;
        const sMax = rawScores.length ? Math.max(...rawScores) : 0;
        const sRange = sMax - sMin;
        const normalized = opts
          .map((opt) => {
            if (typeof opt !== 'object') {
              return { value: String(opt), label: String(opt), score: null };
            }
            const value = opt.value || opt.optionValue || opt.code || opt.name || '';
            const score = typeof opt.score === 'number' ? opt.score : null;
            let label = opt.label || opt.displayName || opt.name || opt.value || '';
            if (score != null) {
              const pct = sRange > 0 ? Math.round(((score - sMin) / sRange) * 100) : 100;
              // Suggested options keep their own label (why the location was suggested).
              label = opt.suggested ? `${label} (${pct}% match)` : `${value} (${pct}% match)`;
            }
            return { value, label, score };
          })
          // Sort by score desc when present (AI suggestions). Untyped scores
          // keep their original order.
          .sort((a, b) => {
            if (a.score == null && b.score == null) return 0;
            if (a.score == null) return 1;
            if (b.score == null) return -1;
            return b.score - a.score;
          });
        const currentOption = normalized.find((o) => o.value === value) || null;
        return (
          <Autocomplete
            freeSolo
            size="small"
            options={normalized}
            value={currentOption || (value || '')}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt?.label || opt?.value || ''))}
            renderOption={(props, opt) => (
              <li {...props} key={opt.value}>{opt.label || opt.value}</li>
            )}
            isOptionEqualToValue={(o, v) => {
              const ov = typeof o === 'string' ? o : o?.value;
              const vv = typeof v === 'string' ? v : v?.value;
              return ov === vv;
            }}
            onChange={(_, newVal) => {
              const raw = newVal == null ? '' : (typeof newVal === 'string' ? newVal : newVal.value);
              const v = applyTransform(raw);
              onChange(field.fieldName, v);
              // A real option was picked from the list (not free-typed) -- the picker's
              // own options are already known-valid (they came straight from the
              // entity's options_script), so validate immediately instead of waiting
              // for a native blur event that may never fire (MUI often keeps focus on
              // the input after a click-select). Otherwise a stale error from an
              // earlier failed attempt keeps Submit disabled indefinitely.
              if (newVal != null && typeof newVal !== 'string' && onBlur) {
                onBlur(field, v);
              }
            }}
            onInputChange={(_, inputVal, reason) => {
              if (reason === 'input') {
                onChange(field.fieldName, applyTransform(inputVal));
                searchDynamicOptions(field, inputVal);
              }
            }}
            onOpen={() => { if ((dynamicOptions[field.fieldName] || []).length === 0) searchDynamicOptions(field, value || ''); }}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                required={!isLine && !!field.isRequired}
                label={isLine ? undefined : field.displayLabel}
                placeholder={field.placeholder || ''}
                onBlur={() => {
                  if (!onBlur) return;
                  // The stored value can end up holding the OPTION'S LABEL rather
                  // than its real value -- MUI freeSolo keeps whatever text is in
                  // the box as-is on blur, it doesn't auto-resolve to the matching
                  // option unless the user explicitly clicks/selects it. If that
                  // text happens to match a known option's label (e.g. "Jane Doe
                  // (jane@co.com)"), snap it back to the option's real value
                  // (e.g. the userId) before validating -- otherwise a
                  // label-shaped string that visually looks like a valid pick
                  // fails validation and leaves Submit disabled with no clear
                  // indication why.
                  if (value && !normalized.some((o) => o.value === value)) {
                    const matched = normalized.find(
                      (o) => o.label && String(o.label).toLowerCase() === String(value).toLowerCase()
                    );
                    if (matched) {
                      onChange(field.fieldName, matched.value);
                      onBlur(field, matched.value);
                      return;
                    }
                  }
                  onBlur(field, value);
                }}
                error={!!error}
                helperText={
                  isValidating
                    ? 'Validating...'
                    // a plain list (e.g. every location) isn't "AI suggestions": show the field's own help
                    : (error || infoMessage || (suggestedCount > 0
                      ? `${suggestedCount} suggested location${suggestedCount === 1 ? '' : 's'} — pick one or type your own`
                      : (rawScores.length > 0
                        ? `${normalized.length} AI suggestion${normalized.length === 1 ? '' : 's'} — pick one or type your own`
                        : (field.description || ''))))
                }
                sx={commonSx}
              />
            )}
          />
        );
      }

      case 'NUMBER':
      case 'DECIMAL':
        // Send a real JS number, not the raw string from the DOM input -- see
        // the matching fix in EntityEditOverlay.js for the full rationale
        // (WMS BigDecimal/Integer fields set via direct property assignment
        // on the Grails side throw a ClassCastException on a String value).
        return (
          <TextField
            fullWidth size="small" type="number"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            placeholder={isLine ? (field.placeholder || field.displayLabel) : undefined}
            value={value ?? ''} // 0 is a value, not a blank
            onChange={(e) => {
              const raw = e.target.value;
              onChange(field.fieldName, raw === '' ? '' : Number(raw));
            }}
            onBlur={blurHandler}
            disabled={isDisabled}
            error={!!error}
            helperText={isValidating ? 'Validating...' : (error || (!isLine && field.description))}
            inputProps={{ min: field.minValue, max: field.maxValue, step: field.fieldType === 'DECIMAL' ? 'any' : 1 }}
            sx={commonSx}
          />
        );

      case 'DATE':
        return (
          <TextField
            fullWidth size="small" type="date"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            value={value || ''}
            onChange={(e) => onChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            error={!!error}
            helperText={error || (!isLine && field.description)}
            InputLabelProps={{ shrink: true }}
            sx={commonSx}
          />
        );

      case 'DATETIME':
        return (
          <TextField
            fullWidth size="small" type="datetime-local"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            value={value || ''}
            onChange={(e) => onChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            error={!!error}
            helperText={error || (!isLine && field.description)}
            InputLabelProps={{ shrink: true }}
            sx={commonSx}
          />
        );

      case 'PASSWORD':
        // A secret (FH-387): typed, never shown back; the browser must not offer to fill or save it
        return (
          <TextField
            fullWidth size="small"
            type="password"
            autoComplete="new-password"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            placeholder={field.placeholder || ''}
            value={value || ''}
            onChange={(e) => onChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            error={!!error}
            helperText={error || (!isLine && field.description)}
            inputProps={{ 'data-lpignore': 'true', spellCheck: false }}
            InputLabelProps={{ shrink: true }}
            sx={commonSx}
          />
        );

      case 'TEXTAREA':
        const textareaValue = (typeof value === 'object' && value !== null)
          ? JSON.stringify(value, null, 2)
          : (value || '');
        return (
          <TextField
            fullWidth size="small"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            placeholder={isLine ? (field.placeholder || field.displayLabel) : (field.placeholder || '')}
            value={textareaValue}
            onChange={(e) => onChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            multiline minRows={isLine ? 1 : 3} maxRows={12}
            error={!!error}
            helperText={error || (!isLine && field.description)}
            sx={commonSx}
          />
        );

      case 'BOOLEAN':
        return (
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === 'true'}
                  onChange={(e) => onChange(field.fieldName, e.target.checked)}
                  disabled={isDisabled}
                  sx={{
                    color: colors.textMuted,
                    '&.Mui-checked': { color: '#22c55e' },
                  }}
                />
              }
              label={field.displayLabel}
              sx={{ color: colors.textPrimary }}
            />
            {!isLine && field.description && (
              <FormHelperText sx={{ color: colors.textMuted, mt: -0.5, ml: 4 }}>{field.description}</FormHelperText>
            )}
          </Box>
        );

      case 'PICKER': {
        // Resolve pickerConfig: from $ref resolution cache, inline config, or field.pickerConfig
        let pConfig = field.pickerConfig;
        if (!pConfig && field.allowedValues) {
          try {
            const parsed = typeof field.allowedValues === 'string' ? JSON.parse(field.allowedValues) : field.allowedValues;
            if (parsed && parsed['$ref']) {
              pConfig = resolvedPickers[parsed['$ref']] || null;
            } else {
              pConfig = resolvedPickers[field.fieldName] || parsed;
            }
          } catch (e) { /* ignore parse errors */ }
        }
        return (
          <TextField
            fullWidth size="small"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            placeholder={isLine ? (field.placeholder || field.displayLabel) : (field.placeholder || '')}
            value={value || ''}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            onBlur={blurHandler}
            disabled={isDisabled}
            error={!!error}
            helperText={isValidating ? 'Validating...' : (error || (!isLine && field.description))}
            InputProps={{
              endAdornment: pConfig ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={`Search ${field.displayLabel}`}
                    onClick={() => { setPickerField({ ...field, pickerConfig: pConfig }); setPickerOpen(true); }}
                    sx={{ color: colors.textMuted, '&:hover': { color: '#22c55e' } }}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              ...commonSx,
              ...(field.textTransform === 'UPPERCASE' ? { '& .MuiInputBase-input': { ...commonSx['& .MuiInputBase-input'], textTransform: 'uppercase' } } : {}),
            }}
          />
        );
      }

      default: { // TEXT, LINK, REFERENCE
        // Check for generateScript in allowedValues (parsed as JSON object)
        let generateConfig = null;
        if (field.allowedValues && typeof field.allowedValues === 'object' && !Array.isArray(field.allowedValues) && field.allowedValues.generateScript) {
          generateConfig = field.allowedValues;
        } else if (typeof field.allowedValues === 'string') {
          try {
            const parsed = JSON.parse(field.allowedValues);
            if (parsed.generateScript) generateConfig = parsed;
          } catch (e) { /* not JSON */ }
        }

        // Enforce the real WMS column length at the UI layer (field.maxLength, from
        // entity_field_metadata.max_length) instead of only finding out via a 500 from
        // WMS after submit -- e.g. Area.areaId's real maxSize is 15 characters, and a
        // descriptive name typed straight into that field previously just failed
        // silently/unclearly server-side. inputProps maxLength stops the browser from
        // typing past the limit at all; the helperText below surfaces a live count once
        // the user is within 10 characters of it, same spot the validation error/AI-hint
        // text already uses.
        const charCount = typeof value === 'string' ? value.length : 0;
        const nearLimit = field.maxLength && charCount >= field.maxLength - 10;
        const textField = (
          <TextField
            fullWidth size="small"
            required={!isLine && !!field.isRequired}
            label={isLine ? undefined : field.displayLabel}
            placeholder={isLine ? (field.placeholder || field.displayLabel) : (field.placeholder || '')}
            value={value || ''}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            onBlur={blurHandler}
            disabled={isDisabled}
            error={!!error}
            helperText={
              isValidating
                ? 'Validating...'
                : (error || (nearLimit ? `${charCount}/${field.maxLength} characters` : (!isLine && field.description)))
            }
            inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
            sx={{
              ...commonSx,
              ...(field.textTransform === 'UPPERCASE' ? { '& .MuiInputBase-input': { ...commonSx['& .MuiInputBase-input'], textTransform: 'uppercase' } } : {}),
            }}
          />
        );

        if (generateConfig) {
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: isLine ? 0 : 2 }}>
              <Box sx={{ flex: 1 }}>{textField}</Box>
              <Tooltip title="Generate">
                <IconButton
                  size="small"
                  sx={{
                    mt: 0.5, bgcolor: 'rgba(34, 197, 94, 0.1)', border: `1px solid ${colors.border}`,
                    borderRadius: 0, width: 36, height: 36,
                    '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)', borderColor: '#22c55e' },
                  }}
                  onClick={async () => {
                    try {
                      const resp = await FlexService.executeScript({ scriptName: generateConfig.generateScript, context: {} });
                      if (resp.success) {
                        const genValue = resp.context?.result?.[generateConfig.generateField || 'lpn'] || resp.context?.result?.lpn || resp.context?.result;
                        if (genValue && typeof genValue === 'string') {
                          handleChange(field.fieldName, genValue);
                        } else if (genValue && typeof genValue === 'object') {
                          handleChange(field.fieldName, genValue[generateConfig.generateField || 'lpn'] || JSON.stringify(genValue));
                        }
                      }
                    } catch (err) {
                      console.error('Generate failed:', err);
                    }
                  }}
                >
                  <AddIcon fontSize="small" sx={{ color: '#22c55e' }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        return textField;
      }
    }
  };

  return (
    <Box
      {...enterAdvanceProps(() => { if (!saving) handleSave(); })}
      sx={{
        ...(embedded ? { flex: 1, minHeight: 0, overflow: 'hidden' } : { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }),
        bgcolor: colors.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2, py: 1.5,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {title || (isEdit ? `Edit ${entityLabel}` : `New ${entityLabel}`)}
        </Typography>
        {!embedded && (
          <IconButton onClick={onClose} sx={{ color: colors.textSecondary }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ color: '#22c55e' }} />
          </Box>
        ) : (
          <>
            {/* Header fields */}
            <Box sx={{ p: 3, maxWidth: 640 }}>
              {!fieldNames && (
                <Typography variant="overline" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
                  {entityLabel} Details
                </Typography>
              )}
              {headerFields.filter((field) => isFieldShownFor(field, headerValues)).map((field) => {
                // Apply dynamic overrides (e.g., isRequired from item validation)
                const override = dynamicFieldOverrides[field.fieldName];
                const effectiveField = override ? { ...field, ...override } : field;
                return (
                  <Box key={field.fieldName} data-field={field.fieldName}>
                    {renderField(
                      effectiveField,
                      headerValues[field.fieldName],
                      (fieldName, value) => handleHeaderChange(fieldName, value),
                      errors[field.fieldName],
                      false,
                      (f, v) => handleFieldBlur(f, v),
                      validating[field.fieldName] || false,
                    )}
                    {fieldHints[field.fieldName] && (
                      <Typography data-hint={field.fieldName} variant="body2"
                        sx={{ color: colors.textPrimary, borderLeft: '3px solid #22c55e', pl: 1.5, py: 0.5, mt: -1, mb: 2 }}>
                        {fieldHints[field.fieldName]}
                      </Typography>
                    )}
                  </Box>
                );
              })}

              {mayAddCustomField && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddFieldOpen(true)}
                  sx={{ textTransform: 'none', color: colors.textSecondary, mb: 1 }}
                >
                  Add custom field
                </Button>
              )}

              {/* Dependent fields loaded dynamically (e.g., scenario parameters) */}
              {dependentFieldsLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>Loading parameters...</Typography>
                </Box>
              )}
              {dependentFields.length > 0 && (
                <>
                  <Divider sx={{ borderColor: colors.border, my: 1.5 }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
                    Parameters
                  </Typography>
                  {dependentFields.map((df) => {
                    const dynField = {
                      ...df,
                      isEditable: true,
                      isVisibleInForm: true,
                    };
                    return renderField(
                      dynField,
                      headerValues[df.fieldName] ?? df.defaultValue ?? '',
                      (fn, value) => handleHeaderChange(fn, value),
                      errors[df.fieldName],
                      false,
                    );
                  })}
                </>
              )}
            </Box>

            {/* Detail lines section */}
            {detailEntityName && lineFields.length > 0 && (
              <>
                <Divider sx={{ borderColor: colors.border }} />
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="overline" sx={{ color: colors.textMuted }}>
                      {detailEntityName === 'ReceiptLine' ? 'Receipt Lines' : detailEntityName === 'ReceiveInventoryLine' ? 'Items to Receive' : `${detailEntityName}s`}
                      {lines.length > 0 && (
                        <Typography component="span" variant="caption" sx={{ color: colors.textMuted, ml: 1 }}>
                          ({lines.length} {lines.length === 1 ? 'line' : 'lines'})
                        </Typography>
                      )}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addLine}
                      sx={{
                        color: '#22c55e',
                        textTransform: 'none',
                        borderRadius: 0,
                        fontSize: '0.8rem',
                        '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
                      }}
                    >
                      Add Line
                    </Button>
                  </Box>

                  {/* Column headers */}
                  {lines.length > 0 && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `${lineFields.map(() => '1fr').join(' ')} 40px`,
                        gap: 1,
                        mb: 0.5,
                        px: 0.5,
                      }}
                    >
                      {lineFields.map((f) => (
                        <Typography key={f.fieldName} variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.7rem' }}>
                          {f.displayLabel}{f.isRequired ? ' *' : ''}
                        </Typography>
                      ))}
                      <Box />
                    </Box>
                  )}

                  {/* Line rows */}
                  {lines.map((line, idx) => (
                    <Box
                      key={line._lineId}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `${lineFields.map(() => '1fr').join(' ')} 40px`,
                        gap: 1,
                        mb: 1,
                        p: 0.5,
                        bgcolor: idx % 2 === 0 ? 'transparent' : colors.surfaceElevated,
                        borderRadius: 0,
                        '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.04)' },
                      }}
                    >
                      {lineFields.map((f) => {
                        const errKey = `${idx}.${f.fieldName}`;
                        return (
                          <Box key={f.fieldName}>
                            {renderField(
                              f,
                              line[f.fieldName],
                              (fieldName, value) => handleLineChange(idx, fieldName, value),
                              lineErrors[errKey],
                              true,
                              (field, value) => handleFieldBlur(field, value, errKey),
                              validating[errKey] || false,
                            )}
                          </Box>
                        );
                      })}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Tooltip title="Remove line">
                          <IconButton
                            size="small"
                            onClick={() => removeLine(idx)}
                            disabled={lines.length <= 1}
                            sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' }, mt: 0.25 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}

                  {lines.length === 0 && (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: colors.textMuted, mb: 1 }}>
                        No lines added yet
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addLine}
                        sx={{ color: '#22c55e', textTransform: 'none', borderRadius: 0 }}
                      >
                        Add First Line
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      {!loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2, py: 1.5,
            borderTop: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
          }}
        >
          <Box>
            {saveError && (
              <Typography variant="caption" sx={{ color: '#ef4444' }}>
                {saveError}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!embedded && (
              <Button
                onClick={onClose}
                disabled={saving}
                sx={{ color: colors.textSecondary, borderRadius: 0, textTransform: 'none' }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || Object.keys(errors).length > 0 || Object.keys(lineErrors).length > 0 ||
                // NOT gated on Object.values(validating).some(Boolean) -- an in-flight
                // async field validation (e.g. from the blur a submit click itself
                // causes by moving focus off a still-focused field) would otherwise flip
                // this button disabled in the same tick the click fires, silently
                // swallowing it (FH-110, 2026-10-13: "Load to Trailer" submit did
                // nothing on the first click). handleSave already independently
                // re-runs and awaits every field's async validation before saving, so
                // this flag was redundant with a real race-condition cost.
                headerFields.some((f) => {
                  const isRequired = dynamicFieldOverrides[f.fieldName]?.isRequired ?? f.isRequired;
                  // a field hidden by its condition (visibleWhen) is not asked, so it cannot hold the form back
                  if (!isRequired || f.isEditable === false || !isFieldShownFor(f, headerValues)) return false;
                  const val = headerValues[f.fieldName];
                  return val === undefined || val === null || val === '';
                })
              }
              sx={{
                bgcolor: '#22c55e',
                color: '#fff',
                borderRadius: 0,
                textTransform: 'none',
                '&:hover': { bgcolor: '#16a34a' },
                '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted },
              }}
            >
              {saving ? 'Saving...' : (saveLabel || 'Save')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Inline Entity Picker for PICKER field type */}
      {pickerOpen && pickerField && pickerField.pickerConfig && (
        <EntityPickerOverlay
          pickerConfig={pickerField.pickerConfig}
          parentRowData={headerValues}
          colors={colors}
          onSubmit={(selectedRows) => {
            if (selectedRows && selectedRows.length > 0) {
              const selected = selectedRows[0];
              const pk = pickerField.pickerConfig.primaryKey || 'id';
              const displayKey = pickerField.pickerConfig.displayField || pk;
              const selectedValue = selected[displayKey] || selected[pk];
              const autoFills = { [pickerField.fieldName]: selectedValue };

              // Auto-fill fields with matching names from selected row
              Object.entries(selected).forEach(([k, v]) => {
                if (headerFields.some(f => f.fieldName === k)) {
                  autoFills[k] = v;
                }
              });

              // Apply explicit field mapping from picker config
              // Maps source field (from selected row) → target field (on form)
              const mapping = pickerField.pickerConfig?.fieldMapping;
              if (mapping) {
                Object.entries(mapping).forEach(([sourceField, targetField]) => {
                  if (selected[sourceField] !== undefined && headerFields.some(f => f.fieldName === targetField)) {
                    autoFills[targetField] = selected[sourceField];
                  }
                });
              }

              setHeaderValues(prev => ({ ...prev, ...autoFills }));
              // Trigger validation on the picker field after selection
              if (pickerField.validationScript && selectedValue) {
                handleFieldBlur(pickerField, selectedValue);
              }
            }
            setPickerOpen(false);
            setPickerField(null);
          }}
          onClose={() => { setPickerOpen(false); setPickerField(null); }}
        />
      )}

      <AddCustomFieldDialog
        open={addFieldOpen}
        entityName={entityName}
        entityLabel={entityLabel}
        colors={colors}
        onCancel={() => setAddFieldOpen(false)}
        onAdded={(added) => {
          addedFieldRef.current = added.fieldName || null;
          setAddFieldOpen(false);
          setFieldsVersion((v) => v + 1);
        }}
      />
    </Box>
  );
};

export default EntityWizardOverlay;
